import React from 'react';
import PropTypes from 'prop-types';
import { CardContent, makeStyles, Tooltip, Typography } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Card } from '../../atoms/Card';
import { palette } from '../../../settings/themes/colors';

/**
 * CardStatPanel
 *
 * Flexible 3-zone panel for the dashboard: header (title + optional info icon),
 * body (children) and footer (free ReactNode). Mirrors the visual chrome of
 * BeneficiariesDoubleBarChart so any chart/composition can adopt the same
 * card style by wrapping its content here.
 *
 * Props:
 *   title             string|ReactNode    Header title. Pass <FormattedMessage/> if i18n is needed.
 *   infoTooltip       string|ReactNode    Tooltip text shown when hovering the info icon (the icon
 *                                         only renders when this is set). Pass <FormattedMessage/> if needed.
 *   children          ReactNode           Body content.
 *   footer            ReactNode           Footer content (free composition).
 *   className         string              Override of the root Card.
 *   contentClassName  string              Override of the inner CardContent.
 */

const useStyles = makeStyles({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  cardContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 20px 8px 20px',
    '&:last-child': { paddingBottom: 8 }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 16
  },
  title: {
    color: palette.captudataBlue,
    fontFamily: 'Nunito',
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: '0.15px',
    lineHeight: 1
  },
  infoIcon: {
    fontSize: 16,
    color: palette.dimGray,
    cursor: 'pointer'
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  footer: {
    flexShrink: 0,
    paddingTop: 8
  }
});

export const CardStatPanel = ({ title, infoTooltip, children, footer, className, contentClassName }) => {
  const classes = useStyles();
  const hasHeader = title != null || infoTooltip != null;

  return (
    <Card className={className || classes.card}>
      <CardContent className={contentClassName || classes.cardContent}>
        {hasHeader && (
          <div className={classes.header}>
            {title != null && <Typography className={classes.title}>{title}</Typography>}
            {infoTooltip != null && (
              <Tooltip title={infoTooltip} arrow>
                <InfoOutlinedIcon data-testid="help-icon" className={classes.infoIcon} />
              </Tooltip>
            )}
          </div>
        )}

        <div className={classes.body}>{children}</div>

        {footer && <div className={classes.footer}>{footer}</div>}
      </CardContent>
    </Card>
  );
};

CardStatPanel.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  infoTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string
};

CardStatPanel.defaultProps = {
  title: null,
  infoTooltip: null,
  children: null,
  footer: null,
  className: null,
  contentClassName: null
};
